<template>
  <div class="card production-controller-card" :class="{ editing }">
    <div class="fas fa-grip-vertical" />

    <div class="card-info ml-2">
      <template v-if="!editing">
        <div class="controller-title-row">
          <span class="connection-status__indicator" :class="connectionStatusClass" />

          <div class="card-title controller-title">
            {{ primaryLabel }}
          </div>

          <button
            class="tb-btn tb-btn-text sm controller-edit-button"
            title="Edit controller name"
            @click.stop.prevent="openEditing"
          >
            <span class="fas fa-pencil" />
          </button>

          <span class="assignment-pill" :class="assignmentPillClass">
            {{ assignmentStatus }}
          </span>
        </div>

        <div v-if="secondaryLabel" class="card-subtitle controller-device-type">
          {{ secondaryLabel }}
        </div>
      </template>

      <form v-else @submit.prevent="save">
        <input
          ref="name"
          v-model="name"
          class="tb-input"
          type="text"
          maxlength="100"
          placeholder="Controller name"
          @keydown.esc.prevent="cancel"
        >
      </form>
    </div>

    <div v-if="editing" class="card-actions ml-1">
      <button
        title="Save"
        class="tb-btn tb-btn-text medium-em"
        :class="{ disabled: isSaveDisabled }"
        :disabled="isSaveDisabled"
        @click.prevent="save"
      >
        <span class="fas fa-check" />
      </button>

      <button
        title="Cancel"
        class="tb-btn tb-btn-text medium-em"
        :disabled="isSaving"
        @click.prevent="cancel"
      >
        <span class="fas fa-times" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductionControllerCard',

  props: {
    device: {
      type: Object,
      required: true,
    },
    currentProductionId: {
      type: [Number, String],
      required: false,
    },
    isConnected: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editing: false,
      name: this.device.name || this.device.deviceName || '',
    };
  },

  computed: {
    primaryLabel() {
      return this.device.name || this.device.deviceName || `Controller ${this.device.id}`;
    },

    secondaryLabel() {
      if (this.device.type === 'streamDeck') {
        return this.device.deviceMeta?.streamDeck?.typeName
          || this.device.deviceName
          || 'Stream Deck';
      }

      return this.device.deviceName || this.device.type || '';
    },

    connectionStatusClass() {
      return this.isConnected
        ? 'connection-status__indicator--online'
        : 'connection-status__indicator--offline';
    },

    assignmentStatus() {
      const { assignment } = this.device;

      if (!assignment) {
        return 'Unassigned';
      }

      if (this.currentProductionId && String(assignment.productionId) === String(this.currentProductionId)) {
        return 'Assigned';
      }

      if (assignment.production?.title) {
        return `Assigned to ${assignment.production.title}`;
      }

      return 'Assigned elsewhere';
    },

    assignmentPillClass() {
      return this.device.assignment
        ? 'assignment-pill--assigned'
        : 'assignment-pill--unassigned';
    },

    isSaveDisabled() {
      const trimmedName = this.name.trim();

      return this.isSaving || !trimmedName || trimmedName === this.primaryLabel;
    },
  },

  watch: {
    'device.name': function syncName(name) {
      if (!this.editing) {
        this.name = name || this.device.deviceName || '';
      }
    },
    isSaving(isSaving) {
      if (!isSaving && this.editing && this.device.name === this.name.trim()) {
        this.editing = false;
      }
    },
  },

  methods: {
    openEditing() {
      this.name = this.device.name || this.device.deviceName || '';
      this.editing = true;

      this.$nextTick(() => {
        this.$refs.name.focus();
        this.$refs.name.select();
      });
    },

    save() {
      if (this.isSaveDisabled) return;

      this.$emit('save', {
        deviceId: this.device.id,
        name: this.name.trim(),
      });
    },

    cancel() {
      if (this.isSaving) return;

      this.name = this.device.name || this.device.deviceName || '';
      this.editing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.production-controller-card {
  .card-info {
    min-width: 0;
    flex: 1;
  }
}

.controller-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.controller-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
  color: #fff;
}

.controller-edit-button {
  flex: 0 0 auto;
}

.controller-device-type {
  margin-top: 0.2rem;
  margin-left: 1rem;
  font-size: 0.75rem;
  line-height: 1.2;
  color: #7f8791;
}

.connection-status__indicator {
  flex: 0 0 auto;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;

  &--offline {
    background: #e65447;
  }

  &--online {
    background: #00d483;
  }
}

.assignment-pill {
  flex: 0 0 auto;
  max-width: 45%;
  margin-left: auto;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.7rem;
  line-height: 1.2;
  font-weight: 600;

  &--assigned {
    color: #a7c8b3;
    background: rgba(73, 111, 87, 0.35);
  }

  &--unassigned {
    color: #8e9298;
    background: rgba(92, 96, 102, 0.22);
  }
}
</style>
